import React, { useMemo, useState } from 'react';
import { Box, TextField, Button, Autocomplete } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const FiberPopFilter = ({
  selectedFiberPop,
  setSelectedFiberPop,
  mapRef,
  fiberPopData,
  viewType,
  showFilter,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleClear = () => {
    console.log('Clear button clicked');
    // Clear the filter input - this will trigger useEffect in map component to clear highlight
    setSelectedFiberPop('');
    setInputValue(''); // Also clear the input value for Autocomplete

    // Reset map zoom and position (similar to IpranMap handleRefresh)
    if (viewType === 'map' && mapRef && mapRef.current) {
      // Use clearAll if available (similar to IpranMap pattern)
      if (mapRef.current.clearAll && typeof mapRef.current.clearAll === 'function') {
        console.log('Calling clearAll from Clear button');
        // Use requestAnimationFrame to ensure smooth UI update
        requestAnimationFrame(() => {
          mapRef.current.clearAll();
        });
      } else {
        // Fallback to individual methods
        const mapInstance = mapRef.current.current || mapRef.current;

        if (mapInstance && typeof mapInstance.flyTo === 'function') {
          // Zoom out to default view with smooth easing
          mapInstance.flyTo({
            center: [69.3451, 30.3753], // Default center
            zoom: 5, // Default zoom
            duration: 2000, // Smooth animation
            essential: true, // This animation is essential and should not be interrupted
            easing: (t) => {
              // Ease-out cubic function for smooth deceleration
              return 1 - Math.pow(1 - t, 3);
            },
          });
        }

        // Also explicitly call clearHighlight as backup
        if (
          mapRef.current &&
          mapRef.current.clearHighlight &&
          typeof mapRef.current.clearHighlight === 'function'
        ) {
          console.log('Calling clearHighlight from Clear button');
          // Delay clearHighlight to not interfere with smooth animation
          setTimeout(() => {
            mapRef.current.clearHighlight();
          }, 500);
        }
      }
    }
  };
  const handleApply = () => {
    if (!selectedFiberPop || !fiberPopData.length) {
      return;
    }

    // Find the fiberpop in the data
    const foundPop = fiberPopData.find(
      (pop) => pop.fiber_pop?.toLowerCase() === selectedFiberPop.toLowerCase()
    );

    if (!foundPop) {
      // Could show an error message here
      console.warn(`Fiber POP "${selectedFiberPop}" not found`);
      return;
    }

    // Only navigate map if in map view
    if (viewType === 'map' && mapRef && mapRef.current) {
      const lng = parseFloat(foundPop.long);
      const lat = parseFloat(foundPop.latt);

      if (!isNaN(lng) && !isNaN(lat)) {
        // Use the flyTo method from mapRef if available, otherwise use the map instance directly
        if (mapRef.current.flyTo && typeof mapRef.current.flyTo === 'function') {
          // Move map to the location using the exposed method
          mapRef.current.flyTo({
            center: [lng, lat],
            zoom: 12,
            duration: 2000,
          });
        } else if (mapRef.current.current && typeof mapRef.current.current.flyTo === 'function') {
          // Fallback: use the map instance directly
          mapRef.current.current.flyTo({
            center: [lng, lat],
            zoom: 12,
            duration: 2000,
          });
        }

        // Trigger marker highlight (this will be handled in the map component)
        if (mapRef.current.triggerMarkerHighlight) {
          mapRef.current.triggerMarkerHighlight(foundPop.fiber_pop);
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApply();
    }
  };

  // Get unique list of fiber POP names for autocomplete
  const fiberPopOptions = useMemo(() => {
    if (!fiberPopData || !Array.isArray(fiberPopData)) {
      return [];
    }
    // Extract unique fiber POP names and sort them
    const uniquePops = Array.from(
      new Set(fiberPopData.map((pop) => pop.fiber_pop).filter(Boolean))
    ).sort();
    return uniquePops;
  }, [fiberPopData]);

  const handleAutocompleteChange = (event, newValue) => {
    setSelectedFiberPop(newValue || '');
    setInputValue(newValue || ''); // Update input value when selection changes
    // Auto-apply when user selects from dropdown
    if (newValue && fiberPopData.length) {
      const foundPop = fiberPopData.find(
        (pop) => pop.fiber_pop?.toLowerCase() === newValue.toLowerCase()
      );
      if (foundPop && viewType === 'map' && mapRef && mapRef.current) {
        const lng = parseFloat(foundPop.long);
        const lat = parseFloat(foundPop.latt);
        if (!isNaN(lng) && !isNaN(lat)) {
          // Use the flyTo method from mapRef if available
          if (mapRef.current.flyTo && typeof mapRef.current.flyTo === 'function') {
            mapRef.current.flyTo({
              center: [lng, lat],
              zoom: 12,
              duration: 2000,
            });
          } else if (mapRef.current.current && typeof mapRef.current.current.flyTo === 'function') {
            mapRef.current.current.flyTo({
              center: [lng, lat],
              zoom: 12,
              duration: 2000,
            });
          }
          // Trigger marker highlight
          if (mapRef.current.triggerMarkerHighlight) {
            mapRef.current.triggerMarkerHighlight(foundPop.fiber_pop);
          }
        }
      }
    }
  };

  // Only show filter when showFilter is true and viewType is map
  if (!showFilter || viewType !== 'map') {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '10px',
        left: '32px',
        zIndex: 999,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        mt: '14px',
      }}
    >
      <Autocomplete
        options={fiberPopOptions}
        value={selectedFiberPop || null}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={handleAutocompleteChange}
        onKeyPress={handleKeyPress}
        freeSolo
        size="small"
        sx={{
          width: '220px',
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#fff',
            fontSize: '14px',
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search or Select Fiber POP (e.g., KGP4985)"
            InputProps={{
              ...params.InputProps,
            }}
          />
        )}
        ListboxProps={{
          style: {
            maxHeight: '200px',
          },
        }}
      />
      <Button
        variant="contained"
        onClick={handleApply}
        startIcon={<SearchIcon />}
        sx={{
          backgroundColor: '#BD1D23',
          '&:hover': {
            backgroundColor: '#8B1519',
          },
          textTransform: 'none',
          fontSize: '12px',
          fontWeight: 600,
        }}
      >
        Apply
      </Button>
      <Button
        variant="outlined"
        onClick={handleClear}
        startIcon={<ClearIcon />}
        sx={{
          borderColor: '#BD1D23',
          color: '#BD1D23',
          '&:hover': {
            borderColor: '#8B1519',
            backgroundColor: 'rgba(189, 29, 35, 0.08)',
          },
          textTransform: 'none',
          fontSize: '12px',
          fontWeight: 600,
        }}
      >
        Clear
      </Button>
    </Box>
  );
};

export default FiberPopFilter;
